<script setup lang="ts">
import { computed } from 'vue'

import { useTheme } from './useTheme'

import { Icon } from '@/shared/ui/icon'

// No `useI18n` here — `shared/ui` primitives never import vue-i18n directly (contract 04 §9);
// the caller translates and passes the label, same as `Modal`/`Drawer`'s `closeLabel`. The raw
// key-path defaults are a dev-visible marker, not real copy, for a caller that forgets to pass one.
const props = withDefaults(defineProps<{ lightLabel?: string; darkLabel?: string }>(), {
  lightLabel: 'ui.theme.switchToLight',
  darkLabel: 'ui.theme.switchToDark',
})

const { resolvedTheme, setTheme } = useTheme()

const isDark = computed(() => resolvedTheme.value === 'dark')
const label = computed(() => (isDark.value ? props.lightLabel : props.darkLabel))

function toggle(): void {
  setTheme(isDark.value ? 'light' : 'dark')
}
</script>

<template>
  <button
    type="button"
    class="theme-toggle relative inline-flex h-[1.875rem] w-14 flex-none cursor-pointer items-center rounded-full border-0 bg-border-strong p-0 outline-none transition-colors duration-150 focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)]"
    :class="{ 'is-dark bg-primary': isDark }"
    role="switch"
    :aria-checked="isDark"
    :aria-label="label"
    @click="toggle"
  >
    <Icon
      name="sun"
      :size="15"
      class="theme-toggle__icon theme-toggle__icon--sun relative z-raised ms-[0.375rem] flex-none text-fg"
      aria-hidden="true"
    />
    <Icon
      name="moon"
      :size="15"
      class="theme-toggle__icon theme-toggle__icon--moon relative z-raised ms-auto me-[0.375rem] flex-none text-fg"
      aria-hidden="true"
    />
    <span
      class="theme-toggle__thumb absolute left-[0.1875rem] top-[0.1875rem] h-6 w-6 rounded-full bg-surface shadow-pop transition-transform duration-150"
      :class="{ 'translate-x-[1.625rem]': isDark }"
      aria-hidden="true"
    />
  </button>
</template>
